import { Fragment, type ReactNode } from "react";

/* Минимальный рендер markdown для статей справки: заголовки, абзацы, списки, **жирный**, `код`, [ссылки](url).
   HTML из текста не вставляется — только текст. */

const INLINE = /(\*\*[^*]+\*\*|`[^`]+`|\[[^\]]+\]\((?:https?:\/\/|\/|#)[^)\s]+\))/g;

export function inline(text: string): ReactNode[] {
  const out: ReactNode[] = [];
  let last = 0;
  let i = 0;
  for (const m of text.matchAll(INLINE)) {
    const s = m[0];
    if (m.index! > last) out.push(text.slice(last, m.index));
    if (s.startsWith("**")) out.push(<b key={i++}>{s.slice(2, -2)}</b>);
    else if (s.startsWith("`")) out.push(<code key={i++} className="rounded bg-background-100 px-1 py-0.5 text-[12px]">{s.slice(1, -1)}</code>);
    else {
      const [, label, href] = s.match(/^\[([^\]]+)\]\(([^)]+)\)$/) ?? [];
      out.push(
        <a key={i++} href={href} target={href?.startsWith("http") ? "_blank" : undefined} rel="noreferrer" className="text-primary underline">
          {label}
        </a>,
      );
    }
    last = m.index! + s.length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

type Block = { kind: "h"; level: number; text: string } | { kind: "p"; text: string } | { kind: "ul" | "ol"; items: string[] } | { kind: "code"; text: string };

export function parse(md: string): Block[] {
  const blocks: Block[] = [];
  const lines = md.replace(/\r/g, "").split("\n");
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (!line.trim()) {
      i++;
      continue;
    }
    const h = line.match(/^(#{1,4})\s+(.*)$/);
    if (h) {
      blocks.push({ kind: "h", level: h[1].length, text: h[2] });
      i++;
      continue;
    }
    if (line.startsWith("```")) {
      const buf: string[] = [];
      i++;
      while (i < lines.length && !lines[i].startsWith("```")) buf.push(lines[i++]);
      i++;
      blocks.push({ kind: "code", text: buf.join("\n") });
      continue;
    }
    const li = line.match(/^(\s*)([-*]|\d+\.)\s+(.*)$/);
    if (li) {
      const kind = /\d/.test(li[2]) ? "ol" : "ul";
      const items: string[] = [];
      while (i < lines.length) {
        const m = lines[i].match(/^(\s*)([-*]|\d+\.)\s+(.*)$/);
        if (m && (/\d/.test(m[2]) ? "ol" : "ul") === kind) {
          items.push(m[3]);
          i++;
        } else if (lines[i].match(/^\s{2,}\S/) && items.length) {
          items[items.length - 1] += ` ${lines[i].trim()}`;
          i++;
        } else break;
      }
      blocks.push({ kind, items });
      continue;
    }
    const buf: string[] = [];
    while (i < lines.length && lines[i].trim() && !/^(#{1,4}\s|```|\s*([-*]|\d+\.)\s)/.test(lines[i])) buf.push(lines[i++].trim());
    blocks.push({ kind: "p", text: buf.join(" ") });
  }
  return blocks;
}

export function Markdown({ text, className }: { text: string; className?: string }) {
  return (
    <div className={className}>
      {parse(text).map((b, i) => {
        if (b.kind === "h") {
          const Tag = (`h${Math.min(b.level + 1, 4)}` as "h2" | "h3" | "h4");
          return (
            <Tag key={i} className={b.level <= 2 ? "mb-1.5 mt-4 text-[14px] font-bold text-foreground first:mt-0" : "mb-1 mt-3 text-[13px] font-semibold text-foreground"}>
              {inline(b.text)}
            </Tag>
          );
        }
        if (b.kind === "code") return <pre key={i} className="my-2 overflow-x-auto rounded-btn bg-background-100 p-2.5 text-[12px]"><code>{b.text}</code></pre>;
        if (b.kind === "p") return <p key={i} className="my-1.5 text-[13px] leading-relaxed text-foreground/90">{inline(b.text)}</p>;
        const L = b.kind === "ul" ? "ul" : "ol";
        return (
          <L key={i} className={`my-1.5 flex flex-col gap-1 pl-5 text-[13px] leading-relaxed text-foreground/90 ${b.kind === "ul" ? "list-disc" : "list-decimal"}`}>
            {b.items.map((it, j) => (
              <li key={j}>
                <Fragment>{inline(it)}</Fragment>
              </li>
            ))}
          </L>
        );
      })}
    </div>
  );
}
